import { useState } from 'react';
import { Link } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import { activeTargetInfo, scim, tenantPath, type ScimEndpoint } from '../../identity/session';
import { isGuid } from '../../identity/format';
import {
  Code,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';

const TARGETS: { value: ScimEndpoint['target']; label: string }[] = [
  { value: 'auth', label: 'auth — nyce-logic-auth tenant' },
  { value: 'nycelogic', label: 'nycelogic — WMS installation' },
];

const httpUrl = (s: string) => {
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};

/** The AUTH provisioning URL the gateway forwards to for an `auth` target tenant. */
export const authProvisioningEndpoint = (authUrl: string, tenantId: string) =>
  `${authUrl.replace(/\/+$/, '')}/api/auth/v1/tenants/${tenantId}/provisioning/`;

/** What Entra ID's "Tenant URL" must be for that tenant. */
export const scimTenantUrl = (scimUrl: string, tenantId: string) =>
  `${scimUrl.replace(/\/+$/, '')}/api/scim/v1/${tenantId}`;

function EndpointForm({
  initial,
  isNew,
  onSaved,
  onClose,
}: {
  initial: ScimEndpoint;
  isNew: boolean;
  onSaved: (e: ScimEndpoint) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const info = activeTargetInfo();
  const valid = !!v.tenantId.trim() && httpUrl(v.endpoint.trim());
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await scim.put(v.tenantId.trim(), { endpoint: v.endpoint.trim(), target: v.target, trace: v.trace })
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'New SCIM endpoint' : `SCIM endpoint ${initial.tenantId}`}
      maxWidth={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!valid}>
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Tenant id"
          value={v.tenantId}
          onChange={(e) => setV({ ...v, tenantId: e.target.value })}
          disabled={!isNew}
          hint="The id in the Entra Tenant URL (…/api/scim/v1/<tenant id>)"
          fieldClassName="nyk-span2"
          required
        />
        <Select
          label="Target"
          value={v.target}
          onChange={(t) => setV({ ...v, target: t as ScimEndpoint['target'] })}
          options={TARGETS}
          fieldClassName="nyk-span2"
        />
        <TextField
          label="Endpoint"
          value={v.endpoint}
          onChange={(e) => setV({ ...v, endpoint: e.target.value })}
          error={v.endpoint && !httpUrl(v.endpoint.trim()) ? 'absolute http(s) URL' : undefined}
          hint={
            v.target === 'auth' && info && isGuid(v.tenantId)
              ? `AUTH tenant: ${authProvisioningEndpoint(info.authUrl, v.tenantId.trim())}`
              : 'Where the gateway forwards SCIM requests for this tenant'
          }
          fieldClassName="nyk-span2"
          required
        />
        {v.target === 'auth' &&
          info &&
          isGuid(v.tenantId) &&
          v.endpoint !== authProvisioningEndpoint(info.authUrl, v.tenantId.trim()) && (
            <div className="nyk-span2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setV({ ...v, endpoint: authProvisioningEndpoint(info.authUrl, v.tenantId.trim()) })
                }
              >
                Use this environment's AUTH
              </Button>
            </div>
          )}
        <div className="nyk-span2">
          <Switch
            checked={v.trace}
            onChange={(trace) => setV({ ...v, trace })}
            label="Trace (log request and response bodies)"
          />
        </div>
      </div>
    </Dialog>
  );
}

/** The SCIM gateway's `endpoints` table of the selected environment: where each tenant's provisioning goes. */
function ScimEndpointsBody() {
  const hub = useHubLogin();
  const canWrite = hasScope(hub?.scopes, SCOPES.scimWrite);
  const list = useAsync(() => scim.list(), []);
  const [editing, setEditing] = useState<{ value: ScimEndpoint; isNew: boolean } | null>(null);
  const toast = useNotice();
  const info = activeTargetInfo();
  const remove = async (e: ScimEndpoint) => {
    try {
      await scim.remove(e.tenantId);
      toast.success(`Removed ${e.tenantId}; the gateway refuses provisioning for it now`);
      await list.reload();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const rows = [...(list.data ?? [])].sort((a, b) => a.tenantId.localeCompare(b.tenantId));
  return (
    <>
      <div className="nyk-actions" style={{ marginBottom: 12 }}>
        {canWrite && (
          <Button
            onClick={() =>
              setEditing({ value: { tenantId: '', endpoint: '', target: 'auth', trace: false }, isNew: true })
            }
          >
            New endpoint
          </Button>
        )}
        <span className="nyk-ui-hint">
          Gateway {info?.scimUrl} · other replicas pick up a change within 10 minutes
        </span>
      </div>
      <ErrorBox error={list.error} />
      {toast.element}
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && (
        <EmptyState
          card
          title="No SCIM endpoints"
          hint="Connect a tenant from its Keys tab, or add one here."
        />
      )}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="scim-endpoints">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Target</th>
                  <th>Endpoint</th>
                  <th>Trace</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.tenantId} data-endpoint={e.tenantId}>
                    <td className="nyk-card-title">
                      {e.target === 'auth' && isGuid(e.tenantId) ? (
                        <Link to={tenantPath(e.tenantId)} className="nyk-link">
                          <Code>{e.tenantId}</Code>
                        </Link>
                      ) : (
                        <Code>{e.tenantId}</Code>
                      )}
                    </td>
                    <td data-label="Target">
                      <Chip size="sm">{e.target}</Chip>
                    </td>
                    <td data-label="Endpoint" className="nyk-url">
                      {e.endpoint}
                    </td>
                    <td data-label="Trace">{e.trace ? 'on' : 'off'}</td>
                    <td className="nyk-row-actions">
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing({ value: e, isNew: false })}
                          >
                            Edit
                          </Button>
                          <ConfirmButton
                            size="sm"
                            variant="danger"
                            message={`Remove the SCIM endpoint of ${e.tenantId}? Provisioning for that tenant stops.`}
                            onConfirm={() => void remove(e)}
                          >
                            Remove
                          </ConfirmButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && (
        <EndpointForm
          initial={editing.value}
          isNew={editing.isNew}
          onClose={() => setEditing(null)}
          onSaved={(e) => {
            setEditing(null);
            toast.success(`Saved ${e.tenantId}`);
            void list.reload();
          }}
        />
      )}
    </>
  );
}

export function ScimEndpointsPage() {
  return (
    <Page
      title="SCIM endpoints"
      description="Where the SCIM gateway forwards each tenant's provisioning (Entra ID → gateway → AUTH or a WMS installation)."
    >
      <TargetShell service="scim">
        <ScimEndpointsBody />
      </TargetShell>
    </Page>
  );
}
